import type { AxiosPromise } from 'axios';
import cookie from 'cookie_js';

import { BusinessHoursPeriod } from '../core/api/menu/menu.types';
import { RestaurantDetail } from '../core/api/restaurant/restaurant.types';
import {
  addTime,
  formatDate,
  isWithinInterval,
  setDate,
  toSeconds,
  zonedToUtc,
} from '../utils/dateTimeUtils';
import { storage } from './services/storageServices';

/* eslint-disable */
const UUID = function (): string {
  let d = new Date().getTime();

  if (window.performance && typeof window.performance.now === 'function') {
    d += performance.now(); // use high-precision timer if available
  }
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

  return uuid;
};
/* eslint-enable */

// Generate a UUID
// http://stackoverflow.com/a/8809472/249879
const DeviceId = function (): string {
  const key = 'flipdish-device-id';
  const uuid =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  try {
    const deviceId: string | null = storage.getItem(key);
    if (deviceId && deviceId.match(uuid)) {
      return deviceId;
    }

    const newDeviceId = UUID();
    setInLocalStorage(key, newDeviceId);
    return newDeviceId;
  } catch (error) {
    console.warn(error);
  }

  // No local storage, make a new device ID each time.
  return UUID();
};

const getOpeningTime = (restaurant: RestaurantDetail): string => {
  const openTimeMinuteString = `00${restaurant.OpenTimeMinute}`.slice(-2);
  const openTimeHourString = `00${restaurant.OpenTimeHour}`.slice(-2);
  return `${openTimeHourString}:${openTimeMinuteString}`;
};

const findAndRemove = function <T>(array: T[], item: T): T[] {
  let dup = array.slice();
  // eslint-disable-next-line eqeqeq
  dup = dup.filter((element: T) => element != item);
  return dup;
};

const getUserMessage = (axiosError: any) => {
  const { response } = axiosError;
  if (!response || !response.data) {
    return 'Network error';
  }
  const { DeveloperMessage, UserMessage } = response.data;
  return DeveloperMessage || UserMessage || 'Unknown Error';
};

const toPromise = function <T>(request: AxiosPromise): Promise<T> {
  return new Promise((resolve, reject) => {
    request.catch(reject);
    request.then((result) => {
      const { data } = result;
      if (data.Success) {
        resolve(data.Data);
      } else {
        reject(data);
      }
    });
  });
};

const { userAgent } = navigator;
const isAndroid = /Android/i.test(userAgent);
const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
const isIOSPhone = /iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
const isFirefox = /firefox/i.test(userAgent);
const isChrome = (window as any).chrome;

// For replacing character elements in a string to JSX
const stringToJSX = (array: string[], fn: Function) => {
  let result: JSX.Element[] = [];
  for (let i = 0; i < array.length; i += 1) {
    const mapping = fn(array[i], i);
    result = result.concat(mapping);
  }
  return result;
};

// For converting timezone from Microsoft format to moment format
const convertTimeZone = (TimeZoneInfoId: string): string => {
  let timeZone = 'GMT';

  switch (TimeZoneInfoId) {
    case 'Samoa Standard Time':
      timeZone = 'Pacific/Samoa';
      break;
    case 'Hawaiian Standard Time':
      timeZone = 'Pacific/Honolulu';
      break;
    case 'Alaskan Standard Time':
      timeZone = 'America/Anchorage';
      break;
    case 'Pacific Standard Time':
      timeZone = 'America/Los_Angeles';
      break;
    case 'Mountain Standard Time':
    case 'U.S. Mountain Standard Time':
      timeZone = 'America/Denver';
      break;
    case 'Mexico Standard Time 2':
      timeZone = 'America/Chihuahua';
      break;
    case 'Central Standard Time':
      timeZone = 'America/Chicago';
      break;
    case 'Canada Central Standard Time':
      timeZone = 'Canada/Central';
      break;
    case 'Mexico Standard Time':
      timeZone = 'America/Mexico_City';
      break;
    case 'Central America Standard Time':
      timeZone = 'America/Guatemala';
      break;
    case 'Eastern Standard Time':
    case 'U.S. Eastern Standard Time':
      timeZone = 'America/New_York';
      break;
    case 'S.A. Pacific Standard Time':
      timeZone = 'America/Lima';
      break;
    case 'Atlantic Standard Time':
      timeZone = 'Canada/Atlantic';
      break;
    case 'SA Western Standard Time':
    case 'S.A. Western Standard Time':
      timeZone = 'America/Caracas';
      break;
    case 'Pacific S.A. Standard Time':
      timeZone = 'America/Santiago';
      break;
    case 'Newfoundland and Labrador Standard Time':
      timeZone = 'Canada/Newfoundland';
      break;
    case 'E. South America Standard Time':
      timeZone = 'Brazil/East';
      break;
    case 'S.A. Eastern Standard Time':
    case 'Greenland Standard Time':
      timeZone = 'America/Buenos_Aires';
      break;
    case 'Azores Standard Time':
      timeZone = 'Atlantic/Azores';
      break;
    case 'Cape Verde Standard Time':
      timeZone = 'Atlantic/Cape_Verde';
      break;
    case 'GMT Standard Time':
    case 'Greenwich Standard Time':
      timeZone = 'Europe/Dublin';
      break;
    case 'Central Europe Standard Time':
      timeZone = 'Europe/Belgrade';
      break;
    case 'Central European Standard Time':
      timeZone = 'Europe/Sarajevo';
      break;
    case 'Romance Standard Time':
      timeZone = 'Europe/Paris';
      break;
    case 'W. Europe Standard Time':
      timeZone = 'Europe/Amsterdam';
      break;
    case 'W. Central Africa Standard Time':
      timeZone = 'Africa/Luanda';
      break;
    case 'E. Europe Standard Time':
      timeZone = 'Europe/Bucharest';
      break;
    case 'Egypt Standard Time':
      timeZone = 'Africa/Cairo';
      break;
    case 'FLE Standard Time':
      timeZone = 'Europe/Helsinki';
      break;
    case 'GTB Standard Time':
      timeZone = 'Europe/Athens';
      break;
    case 'Israel Standard Time':
      timeZone = 'Israel';
      break;
    case 'South Africa Standard Time':
      timeZone = 'Africa/Johannesburg';
      break;
    case 'Russian Standard Time':
      timeZone = 'Europe/Moscow';
      break;
    case 'Arab Standard Time':
      timeZone = 'Asia/Kuwait';
      break;
    case 'E. Africa Standard Time':
      timeZone = 'Africa/Nairobi';
      break;
    case 'Arabic Standard Time':
      timeZone = 'Asia/Baghdad';
      break;
    case 'Iran Standard Time':
      timeZone = 'Asia/Tehran';
      break;
    case 'Arabian Standard Time':
      timeZone = 'Asia/Muscat';
      break;
    case 'Caucasus Standard Time':
      timeZone = 'Asia/Baku';
      break;
    case 'Transitional Islamic State of Afghanistan Standard Time':
      timeZone = 'Asia/Kabul';
      break;
    case 'Ekaterinburg Standard Time':
      timeZone = 'Asia/Yekaterinburg';
      break;
    case 'West Asia Standard Time':
      timeZone = 'Asia/Karachi';
      break;
    case 'India Standard Time':
      timeZone = 'Asia/Kolkata';
      break;
    case 'Nepal Standard Time':
      timeZone = 'Asia/Kathmandu';
      break;
    case 'Central Asia Standard Time':
      timeZone = 'Asia/Dhaka';
      break;
    case 'Sri Lanka Standard Time':
      timeZone = 'Asia/Colombo';
      break;
    case 'N. Central Asia Standard Time':
      timeZone = 'Asia/Almaty';
      break;
    case 'Myanmar Standard Time':
      timeZone = 'Asia/Yangon';
      break;
    case 'SE Asia Standard Time':
      timeZone = 'Asia/Bangkok';
      break;
    case 'North Asia Standard Time':
      timeZone = 'Asia/Krasnoyarsk';
      break;
    case 'China Standard Time':
      timeZone = 'Asia/Hong_Kong';
      break;
    case 'Singapore Standard Time':
      timeZone = 'Asia/Singapore';
      break;
    case 'Taipei Standard Time':
      timeZone = 'Asia/Taipei';
      break;
    case 'W. Australia Standard Time':
      timeZone = 'Australia/Perth';
      break;
    case 'North Asia East Standard Time':
      timeZone = 'Asia/Irkutsk';
      break;
    case 'Korea Standard Time':
      timeZone = 'Asia/Seoul';
      break;
    case 'Tokyo Standard Time':
      timeZone = 'Asia/Tokyo';
      break;
    case 'Yakutsk Standard Time':
      timeZone = 'Asia/Yakutsk';
      break;
    case 'A.U.S. Central Standard Time':
      timeZone = 'Australia/Darwin';
      break;
    case 'Cen. Australia Standard Time':
      timeZone = 'Australia/Adelaide';
      break;
    case 'A.U.S. Eastern Standard Time':
      timeZone = 'Australia/Sydney';
      break;
    case 'E. Australia Standard Time':
      timeZone = 'Australia/Brisbane';
      break;
    case 'Tasmania Standard Time':
      timeZone = 'Australia/Hobart';
      break;
    case 'Vladivostok Standard Time':
      timeZone = 'Asia/Vladivostok';
      break;
    case 'West Pacific Standard Time':
      timeZone = 'Pacific/Guam';
      break;
    case 'Central Pacific Standard Time':
      timeZone = 'Asia/Magadan';
      break;
    case 'Fiji Islands Standard Time':
      timeZone = 'Pacific/Fiji';
      break;
    case 'New Zealand Standard Time':
      timeZone = 'Pacific/Auckland';
      break;
    case 'Tonga Standard Time':
      timeZone = 'Pacific/Tongatapu';
      break;
    default:
      timeZone = 'Europe/Dublin';
  }
  return timeZone;
};

/* eslint-disable */
// TODO move to selector
const isInPeriod = (
  AvailableTimes: BusinessHoursPeriod[],
  TimeZoneInfoId: string,
): boolean => {
  if (!AvailableTimes) {
    return false;
  }

  let isInPeriod = false;
  for (const AvailableTime of AvailableTimes) {
    const timeZone = convertTimeZone(TimeZoneInfoId);

    const startTimeFormatted = formatDate(AvailableTime.StartTime, 'HH:mm:ss');

    const zonedStartTime = zonedToUtc(
      setDate(startTimeFormatted, AvailableTime.DayOfWeek, 'day'),
      timeZone,
    );

    const periodArray = AvailableTime.Period.split(':');

    const seconds = toSeconds(
      +periodArray[0] || 0,
      +periodArray[1] || 0,
      +periodArray[2] || 0,
    );
    const endTime = addTime(zonedStartTime, 'seconds', seconds);

    isInPeriod = isWithinInterval(
      new Date(),
      zonedStartTime,
      endTime,
      'seconds',
    );

    if (isInPeriod) {
      break;
    }

    if (
      AvailableTime.StartTimeEarly != null &&
      AvailableTime.PeriodEarly != null
    ) {
      const startTimeEarlyFormatted = formatDate(
        AvailableTime.StartTimeEarly,
        'HH:mm:ss',
      );

      const zonedStartTimeEarly = zonedToUtc(
        setDate(startTimeEarlyFormatted, AvailableTime.DayOfWeek, 'day'),
        timeZone,
      );

      const periodEarlyArray = AvailableTime.Period.split(':');
      const seconds = toSeconds(
        +periodEarlyArray[0] || 0,
        +periodEarlyArray[1] || 0,
        +periodEarlyArray[2] || 0,
      );

      const endTimeEarly = addTime(zonedStartTimeEarly, 'seconds', seconds);

      isInPeriod = isWithinInterval(
        new Date(),
        endTimeEarly,
        endTimeEarly,
        'seconds',
      );

      if (isInPeriod) {
        break;
      }
    }
  }

  return isInPeriod;
};
/* eslint-enable */

const flipdiv = document.getElementById('flipdish-menu');

type ExtraOptionKeys =
  | 'data-base-href'
  | 'data-cookie-policy-link'
  | 'data-force-app'
  | 'data-full-screen-on-mobile'
  | 'data-initial-screen'
  | 'data-language'
  | 'data-menuid'
  | 'data-offset'
  | 'data-privacy-policy-link'
  | 'data-restaurant'
  | 'data-scrollable-container'
  | 'data-local-server'
  | 'data-store-ids'
  | 'data-terms-link'
  | 'data-theme'
  | 'data-webtoappredirect'
  | 'data-web-order-source'
  /** @description used by external embeds */
  | 'data-initial-language'
  /** @deprecated */
  | 'data-permissions';

export const extraOption = (key: ExtraOptionKeys): string | null => {
  const urlParam = extraOptionUrlParam(key);
  if (urlParam) {
    return urlParam;
  }

  return flipdiv && flipdiv.getAttribute(key);
};

const extraOptionUrlParam = (key: ExtraOptionKeys): string | null => {
  // For security reasons, only allow whitelisted URL params to be accepted.
  const urlParamWhitelist = [
    'data-force-app',
    'data-full-screen-on-mobile',
    'data-initial-language',
    'data-initial-screen',
    'data-language',
    'data-menuid',
    'data-offset',
    'data-scrollable-container',
    'data-store-ids',
    'data-theme',
    'data-webtoappredirect',
    'data-web-order-source',
  ];

  if (!urlParamWhitelist.includes(key)) {
    return '';
  }

  if (!window.hasOwnProperty('URLSearchParams')) {
    return '';
  }

  const urlParams = new URLSearchParams(window.location.search);

  return urlParams.get(key);
};

const setInLocalStorage = (key: string, data: any) => {
  // Try saving in localStorage
  try {
    storage.setItem(key, data);
  } catch (e) {
    // If localStorage full clear and then try again
    if (isQuotaExceeded(e)) {
      storage.clear();

      try {
        storage.setItem(key, data);
      } catch (err) {
        console.log(err.message);
      }
    } else {
      console.log(e.message);
    }
  }
};

const isQuotaExceeded = (e: any) => {
  let quotaExceeded = false;
  if (e) {
    if (e.code) {
      switch (e.code) {
        case 22:
          quotaExceeded = true;
          break;
        case 1014:
          // Firefox
          if (e.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
            quotaExceeded = true;
          }
          break;
        default:
          quotaExceeded = false;
      }
    } else if (e.number === -2147024882) {
      // Internet Explorer 8
      quotaExceeded = true;
    }
  }
  return quotaExceeded;
};

const returnColor = (color: string, fallback: string) => {
  if (color === '') {
    return fallback;
  }
  if (color.charAt(0) !== '#') {
    return `#${color}`;
  }
  return color;
};

const lastPhysicalRestaurantIdKey = (appName: string): string =>
  `flipdish-${appName}-last-pr-id`;

const storeLastPhysicalRestaurantId = (
  appName: string,
  restaurantId: string,
): void => {
  Utilities.setInLocalStorage(
    lastPhysicalRestaurantIdKey(appName),
    restaurantId,
  );
};

const getLastPhysicalRestaurantId = (appName: string): string =>
  storage.getItem(lastPhysicalRestaurantIdKey(appName)) || null;

const getMalformedFlipdishTokenCookie = () => {
  const flipdishCookie = document.cookie
    .split(';')
    .find((cookieString) => cookieString.includes('flipdish-token'));

  const flipdishToken = flipdishCookie
    ? flipdishCookie.trim().split('=').pop()
    : '';

  return decodeURIComponent(flipdishToken);
};

const getFlipdishTokenCookie = () => {
  if (!document.cookie) return '';

  let flipdishToken = '';
  try {
    flipdishToken = cookie.get('flipdish-token');
  } catch (error) {
    flipdishToken = getMalformedFlipdishTokenCookie();
  }

  return flipdishToken;
};

const getLangauageFromStorage = () => {
  const wlID = getWLID();
  return storage.getItem(`flipdish-${wlID}-customer-language`);
};

const setLangauageInStorage = (language) => {
  const wlID = getWLID();
  storage.setItem(`flipdish-${wlID}-customer-language`, language);
};

const removeLangauageFromStorage = () => {
  const wlID = getWLID();
  storage.removeItem(`flipdish-${wlID}-customer-language`);
};

const getAdditonalEmbedHeaders = (isEmbed, hostname) => {
  if (
    window.location.hostname.includes('localhost') ||
    !!hostname ||
    !!isEmbed
  ) {
    return null;
  }
  // Do not send headers if its flipdish domain (this is for table ordering systems).
  if (window.location.hostname.includes('flipdish')) {
    return null;
  }
  const webOrderSource = extraOption('data-web-order-source') || '';
  if (webOrderSource === '') {
    return {
      'Flipdish-Web-Order-Source': 'embed',
    };
  }
  return {
    'Flipdish-Web-Order-Source': webOrderSource,
  };
};

export const getWLID = () => extraOption('data-restaurant');

export const isSmile = () => extraOption('data-web-order-source') === 'smile';

export const isPhoton = () => document.body.dataset?.identifier === 'photon';

export const Utilities = {
  DeviceId,
  UUID,
  convertTimeZone,
  extraOption,
  findAndRemove,
  flipdiv,
  getAdditonalEmbedHeaders,
  getFlipdishTokenCookie,
  getLastPhysicalRestaurantId,
  getOpeningTime,
  getUserMessage,
  isAndroid,
  isChrome,
  isFirefox,
  isIOS,
  isIOSPhone,
  isInPeriod,
  isQuotaExceeded,
  returnColor,
  setInLocalStorage,
  storeLastPhysicalRestaurantId,
  stringToJSX,
  toPromise,
  getLangauageFromStorage,
  setLangauageInStorage,
  removeLangauageFromStorage,
};
